import { inject, Injectable, OnDestroy } from '@angular/core';
import { Observable, BehaviorSubject, Subscription, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';

import { UserModel } from '../models/user.model';
import { jwtDecode } from 'jwt-decode';
import { GenericService } from './generic.service';
import { GlobalDataService } from './global.data.service';

@Injectable({
	providedIn: 'root',
})
export class AuthService extends GenericService<any> implements OnDestroy {
	private unsubscribe: Subscription[] = [];

	currentUser: UserModel | any;

	private router = inject(Router);
	private schedule = null;
	private refreshTimeout: any;
	private globalDataSerivce: GlobalDataService = inject(GlobalDataService);
	constructor(private http: HttpClient) {
		super(http);
	}

	isLoggedIn() {
		return !!this.getToken();
	}

	createAccount(email: string, password: string) {
		return super.post({ data: { email, password }, endpoint: `auth/sign-up` });
	}

	resetPassword(email: string) {
		return super.post({ data: { email }, endpoint: `auth/forgot-password` });
	}

	login(email: string, password: string) {
		const body = new URLSearchParams({
			grant_type: 'password',
			client_id: environment.keycloak.clientId,
			username: email,
			password: password,
		});

		return this.http1
			.post<any>(`${environment.keycloak.url}/realms/${environment.keycloak.realm}/protocol/openid-connect/token`, body.toString(), {
				headers: {
					'Content-Type': 'application/x-www-form-urlencoded',
				},
			})
			.pipe(
				tap((res: any) => {
					sessionStorage.setItem('access_token', res.access_token);
					sessionStorage.setItem('refresh_token', res.refresh_token);
					this.scheduleRefresh(res.expires_in);
				})
			);
	}

	refresh() {
		const body = new URLSearchParams({
			grant_type: 'refresh_token',
			client_id: environment.keycloak.clientId,
			refresh_token: sessionStorage.getItem('refresh_token')!,
		});

		return this.http
			.post(`${environment.keycloak.url}/realms/${environment.keycloak.realm}/protocol/openid-connect/token`, body.toString(), {
				headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
			})
			.pipe(
				tap((res: any) => {
					sessionStorage.setItem('access_token', res.access_token);
					sessionStorage.setItem('refresh_token', res.refresh_token);
					this.scheduleRefresh(res.expires_in);
				})
			);
	}

	scheduleRefresh(expiresIn: number) {
		const exp = expiresIn * 1000;

		const refreshIn = exp - exp * 0.2;

		if (this.refreshTimeout) {
			clearTimeout(this.refreshTimeout);
		}

		if (refreshIn > 0) {
			this.refreshTimeout = setTimeout(() => {
				this.refresh().subscribe({
					error: () => {
						this.logout();
						this.router.navigate(['/login']);
					},
				});
			}, refreshIn);
		}
	}
	abortSchedule() {
		if (this.refreshTimeout) {
			clearTimeout(this.refreshTimeout);
			this.refreshTimeout = null;
		}
	}

	getToken(): string | null {
		return sessionStorage.getItem('access_token');
	}

	logout() {
		this.abortSchedule();
		this.globalDataSerivce.clearData();
		sessionStorage.clear();
	}

	getUser(): UserModel {
		return this.currentUser;
	}

	ngOnDestroy() {
		this.unsubscribe.forEach((sb) => sb.unsubscribe());
	}
}
